import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Ticket, Plus, Percent, DollarSign, PauseCircle, PlayCircle, Copy, TrendingUp, BarChart3, Split, MessageCircle, Mail } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/coupons");
      setCoupons(data);
    } catch (err) {
      toast.error("فشل التحميل: " + (err?.response?.data?.detail || "خطأ"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (pc) => {
    const action = pc.active ? "deactivate" : "activate";
    await api.post(`/admin/coupons/${pc.id}/${action}`);
    toast.success(pc.active ? "تم الإلغاء" : "تم التفعيل");
    load();
  };

  const cloneChannels = async (code) => {
    try {
      const { data } = await api.post(`/admin/coupons/${code}/clone-channels`);
      const c = data.created?.length || 0;
      const e = data.existed?.length || 0;
      if (c > 0) toast.success(`تم إنشاء ${c} نُسخة${e ? ` (${e} كانت موجودة)` : ""}`);
      else toast.info(`النُسختان موجودتان مسبقًا: ${(data.existed || []).join(", ")}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل الإنشاء");
    }
  };

  const tagChannel = async (code, channel) => {
    await api.post(`/admin/coupons/${encodeURIComponent(code)}/tag-channel`,
      null, { params: { channel: channel || "", campaign: channel ? "cart_recovery" : "" } });
    toast.success(channel ? `تم وسم الكود بقناة: ${channel}` : "تمت إزالة الوسم");
    load();
  };

  const totalRevenue = coupons.reduce((sum, c) => sum + (c.stats?.revenue_after_discount || 0), 0);
  const totalRedemptions = coupons.reduce((sum, c) => sum + c.times_redeemed, 0);

  return (
    <div className="space-y-4" data-testid="admin-coupons">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Ticket className="h-6 w-6 text-primary" />
          <h1 className="text-2xl sm:text-3xl font-display font-semibold">أكواد الخصم</h1>
        </div>
        <Button onClick={() => setShowNew(true)} data-testid="coupon-new"><Plus className="h-4 w-4 me-1" /> كود جديد</Button>
      </div>

      {/* Summary */}
      <div className="grid gap-2 sm:grid-cols-3">
        <SummaryCard label="إجمالي الأكواد" value={coupons.length} />
        <SummaryCard label="مرات الاستخدام" value={totalRedemptions} />
        <SummaryCard label="إيراد بعد الخصم (ريال)" value={`${totalRevenue.toLocaleString()} ﷼`} highlight />
      </div>

      {/* Revenue Analytics Chart */}
      <RevenueAnalytics />

      {/* List */}
      {loading && <p className="text-foreground/50 py-6 text-center">جاري التحميل...</p>}
      {!loading && coupons.length === 0 && (
        <Card className="p-8 text-center text-foreground/50">لا يوجد أكواد خصم بعد. أنشئ أول كود من الزر أعلاه.</Card>
      )}
      {coupons.map((c) => {
        const discount = c.percent_off ? `${c.percent_off}%` : `${c.amount_off_sar} ﷼`;
        const expired = c.expires_at && new Date(c.expires_at * 1000) < new Date();
        const exhausted = c.max_redemptions && c.times_redeemed >= c.max_redemptions;
        return (
          <Card key={c.id} className="p-4" data-testid={`coupon-${c.code}`}>
            <div className="flex justify-between items-start gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="text-lg font-bold font-mono bg-primary/10 text-primary px-2 py-1 rounded" dir="ltr">{c.code}</code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(c.code); toast.success("نُسخ"); }}
                    className="text-foreground/40 hover:text-foreground"
                    data-testid={`coupon-copy-${c.code}`}
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  <Badge variant={c.percent_off ? "default" : "secondary"} className="text-xs">
                    {c.percent_off ? <Percent className="h-3 w-3 me-1 inline" /> : <DollarSign className="h-3 w-3 me-1 inline" />}
                    {discount}
                  </Badge>
                  {!c.active && <Badge variant="destructive" className="text-[10px]">معطّل</Badge>}
                  {expired && <Badge variant="outline" className="text-[10px] text-red-700">منتهي</Badge>}
                  {exhausted && <Badge variant="outline" className="text-[10px]">استُنفد</Badge>}
                  {c.attribution?.channel === "whatsapp" && (
                    <Badge className="text-[10px] bg-green-100 text-green-800 hover:bg-green-100"><MessageCircle className="h-3 w-3 me-1 inline" />WhatsApp</Badge>
                  )}
                  {c.attribution?.channel === "email" && (
                    <Badge className="text-[10px] bg-blue-100 text-blue-800 hover:bg-blue-100"><Mail className="h-3 w-3 me-1 inline" />بريد</Badge>
                  )}
                </div>
                {c.name && <p className="text-xs text-foreground/60 mt-1">{c.name}</p>}
                <div className="mt-2 text-xs text-foreground/70 space-y-1">
                  <div>مرات الاستخدام: <strong>{c.times_redeemed}</strong>{c.max_redemptions ? ` / ${c.max_redemptions}` : ""}</div>
                  <div>الإيراد بعد الخصم: <strong className="text-green-700">{(c.stats?.revenue_after_discount || 0).toLocaleString()} ﷼</strong></div>
                  {c.expires_at && <div className="text-foreground/50">ينتهي: {new Date(c.expires_at * 1000).toLocaleDateString("ar")}</div>}
                </div>
              </div>
              <div className="flex flex-col gap-1 items-stretch">
                <Button
                  size="sm"
                  variant={c.active ? "outline" : "default"}
                  onClick={() => toggle(c)}
                  data-testid={`coupon-toggle-${c.code}`}
                >
                  {c.active ? <><PauseCircle className="h-4 w-4 me-1" />تعطيل</> : <><PlayCircle className="h-4 w-4 me-1" />تفعيل</>}
                </Button>
                {!c.attribution?.channel && !c.code.endsWith("WA") && !c.code.endsWith("EM") && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-[11px]"
                    onClick={() => cloneChannels(c.code)}
                    title="ينشئ نسختين: {code}WA للواتساب و {code}EM للبريد — لتنسب الإيراد لكل قناة"
                    data-testid={`coupon-clone-${c.code}`}
                  >
                    <Split className="h-3.5 w-3.5 me-1" />إنشاء نُسختين للقنوات
                  </Button>
                )}
                {!c.attribution?.channel && (c.code.endsWith("WA") || c.code.endsWith("EM")) && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="text-[10px] h-7 flex-1"
                            onClick={() => tagChannel(c.code, c.code.endsWith("WA") ? "whatsapp" : "email")}>
                      وسم يدوي: {c.code.endsWith("WA") ? "WhatsApp" : "بريد"}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>
        );
      })}

      <NewCouponDialog open={showNew} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />
    </div>
  );
}

function SummaryCard({ label, value, highlight }) {
  return (
    <Card className="p-3">
      <div className="text-[10px] text-foreground/60">{label}</div>
      <div className={`text-xl font-semibold ${highlight ? "text-green-700" : ""}`}>{value}</div>
    </Card>
  );
}

// Distinct, high-contrast palette for up to 10 coupon codes on the chart.
const CHART_COLORS = [
  "#0ea5e9", // sky
  "#22c55e", // green
  "#f59e0b", // amber
  "#ef4444", // red
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#14b8a6", // teal
  "#eab308", // yellow
  "#6366f1", // indigo
  "#f97316", // orange
];

function RevenueAnalytics() {
  const [range, setRange] = useState("30d");
  const [variant, setVariant] = useState(""); // "" | "A" | "B"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      try {
        const params = { range };
        if (variant) params.variant = variant;
        const { data: resp } = await api.get("/admin/coupons/revenue-analytics", { params });
        if (alive) setData(resp);
      } catch (err) {
        toast.error("فشل تحميل الإحصائيات: " + (err?.response?.data?.detail || "خطأ"));
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [range, variant]);

  const codes = data?.codes || [];
  const series = data?.series || [];
  const totals = data?.totals || [];
  const hasData = totals.length > 0;
  const barData = totals.slice(0, 10).map((t) => ({ code: t.code, revenue: t.revenue }));

  return (
    <Card className="p-4 space-y-4" data-testid="coupon-revenue-analytics">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-display font-semibold">إيرادات الأكواد</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex gap-1 border rounded-md p-0.5 bg-muted/30" data-testid="analytics-range">
            {[
              { k: "7d", label: "٧ أيام" },
              { k: "30d", label: "٣٠ يوم" },
              { k: "90d", label: "٩٠ يوم" },
              { k: "all", label: "الكل" },
            ].map((opt) => (
              <button
                key={opt.k}
                type="button"
                onClick={() => setRange(opt.k)}
                className={`px-2.5 py-1 text-xs rounded ${range === opt.k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                data-testid={`analytics-range-${opt.k}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1 border rounded-md p-0.5 bg-muted/30" data-testid="analytics-variant">
            {[
              { k: "", label: "الكل" },
              { k: "A", label: "A" },
              { k: "B", label: "B" },
            ].map((opt) => (
              <button
                key={opt.k || "all"}
                type="button"
                onClick={() => setVariant(opt.k)}
                className={`px-2.5 py-1 text-xs rounded ${variant === opt.k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                data-testid={`analytics-variant-${opt.k || "all"}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && <div className="text-center text-sm text-foreground/50 py-8">جاري التحميل...</div>}

      {!loading && !hasData && (
        <div className="text-center text-sm text-foreground/50 py-10 flex flex-col items-center gap-2">
          <BarChart3 className="h-8 w-8 text-foreground/30" />
          لا توجد مبيعات باستخدام أكواد الخصم في هذه الفترة.
        </div>
      )}

      {!loading && hasData && (
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Line chart — revenue over time by code (spans 2 cols) */}
          <div className="lg:col-span-2 space-y-2" data-testid="revenue-timeline-chart">
            <div className="text-xs text-foreground/60 font-medium">الإيراد اليومي حسب الكود (أعلى ١٠)</div>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <LineChart data={series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d?.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} width={45} />
                  <Tooltip
                    contentStyle={{ direction: "rtl", fontSize: 12, borderRadius: 8 }}
                    labelStyle={{ direction: "ltr" }}
                    formatter={(v, name) => [`${v.toLocaleString()} ﷼`, name]}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {codes.map((code, idx) => (
                    <Line
                      key={code}
                      type="monotone"
                      dataKey={code}
                      stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Right column: top-earning bar chart + totals table */}
          <div className="space-y-3">
            <div className="space-y-2" data-testid="revenue-top-chart">
              <div className="text-xs text-foreground/60 font-medium">أعلى الأكواد إيرادًا</div>
              <div style={{ width: "100%", height: 180 }}>
                <ResponsiveContainer>
                  <BarChart data={barData} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="code" tick={{ fontSize: 10 }} width={70} />
                    <Tooltip formatter={(v) => `${v.toLocaleString()} ﷼`} contentStyle={{ direction: "rtl", fontSize: 12, borderRadius: 8 }} />
                    <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                      {barData.map((_, idx) => (
                        <rect key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-md border overflow-hidden" data-testid="revenue-totals-table">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-foreground/60">
                  <tr>
                    <th className="text-start p-2">الكود</th>
                    <th className="text-end p-2">مبيعات</th>
                    <th className="text-end p-2">إيراد</th>
                    {variant === "" && <th className="text-end p-2 hidden sm:table-cell">A / B</th>}
                  </tr>
                </thead>
                <tbody>
                  {totals.slice(0, 6).map((t, idx) => (
                    <tr key={t.code} className="border-t">
                      <td className="p-2">
                        <span className="inline-flex items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 rounded-full inline-block"
                            style={{ background: CHART_COLORS[idx % CHART_COLORS.length] }}
                          />
                          <code className="font-mono" dir="ltr">{t.code}</code>
                        </span>
                      </td>
                      <td className="p-2 text-end">{t.purchases}</td>
                      <td className="p-2 text-end font-semibold text-green-700">{t.revenue.toLocaleString()}</td>
                      {variant === "" && (
                        <td className="p-2 text-end hidden sm:table-cell text-[10px] text-foreground/60" dir="ltr">
                          {t.variant_breakdown?.A?.toLocaleString() || 0} / {t.variant_breakdown?.B?.toLocaleString() || 0}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function NewCouponDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    code: "",
    discount_type: "percent",
    value: 20,
    max_redemptions: "",
    expires_at: "",
    name: "",
    channel: "",
    campaign: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm({ code: "", discount_type: "percent", value: 20, max_redemptions: "", expires_at: "", name: "", channel: "", campaign: "" });
  }, [open]);

  const submit = async () => {
    if (!form.code) return toast.error("اختر كودًا");
    if (!form.value) return toast.error("أدخل قيمة الخصم");
    setSubmitting(true);
    try {
      const payload = { ...form };
      payload.value = parseInt(payload.value);
      if (payload.max_redemptions) payload.max_redemptions = parseInt(payload.max_redemptions);
      else delete payload.max_redemptions;
      if (!payload.expires_at) delete payload.expires_at;
      if (!payload.name) delete payload.name;
      if (!payload.channel) delete payload.channel;
      if (!payload.campaign) delete payload.campaign;
      const { data } = await api.post("/admin/coupons", payload);
      toast.success(`أنشئ الكود: ${data.code}`);
      onCreated();
    } catch (err) {
      toast.error("فشل: " + (err?.response?.data?.detail || "خطأ"));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" dir="rtl">
        <DialogHeader><DialogTitle>كود خصم جديد</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>الكود (سيراه العميل)</Label>
            <Input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, "") })}
              placeholder="مثال: SUMMER50"
              dir="ltr"
              className="uppercase font-mono"
              data-testid="new-coupon-code"
            />
            <p className="text-[10px] text-foreground/50 mt-1">حروف إنجليزية وأرقام فقط. لا يمكن التعديل بعد الحفظ.</p>
          </div>
          <div>
            <Label>نوع الخصم</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, discount_type: "percent", value: 20 })}
                className={`flex-1 py-2 rounded-md border text-sm ${form.discount_type === "percent" ? "bg-primary text-primary-foreground border-primary" : "bg-white"}`}
                data-testid="new-coupon-percent"
              >
                <Percent className="h-3 w-3 inline me-1" /> نسبة مئوية
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, discount_type: "amount", value: 50 })}
                className={`flex-1 py-2 rounded-md border text-sm ${form.discount_type === "amount" ? "bg-primary text-primary-foreground border-primary" : "bg-white"}`}
                data-testid="new-coupon-amount"
              >
                <DollarSign className="h-3 w-3 inline me-1" /> مبلغ ثابت
              </button>
            </div>
          </div>
          <div>
            <Label>{form.discount_type === "percent" ? "نسبة الخصم (1-100)" : "المبلغ بالريال"}</Label>
            <Input
              type="number"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              min={1}
              max={form.discount_type === "percent" ? 100 : 100000}
              data-testid="new-coupon-value"
            />
            {form.discount_type === "amount" && (
              <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-xs p-2 leading-relaxed" data-testid="coupon-amount-warning">
                ⚠️ الخصم بمبلغ ثابت يُطبَّق فقط على المبيعات بالريال السعودي (SAR).
                للحسم على مبيعات دول الخليج (AED / KWD / QAR / OMR / BHD) استخدم <b>نسبة مئوية</b> بدلاً من ذلك — تعمل تلقائيًا في كل العملات.
              </div>
            )}
          </div>
          <div>
            <Label>الحد الأقصى للاستخدام (اختياري)</Label>
            <Input
              type="number"
              value={form.max_redemptions}
              onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })}
              placeholder="بدون حد"
              min={1}
              data-testid="new-coupon-max"
            />
          </div>
          <div>
            <Label>تاريخ الانتهاء (اختياري)</Label>
            <Input
              type="datetime-local"
              value={form.expires_at ? form.expires_at.slice(0, 16) : ""}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value ? e.target.value + ":00Z" : "" })}
              data-testid="new-coupon-expires"
            />
          </div>
          <div>
            <Label>الوصف (داخلي، اختياري)</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="مثال: خصم الصيف"
            />
          </div>
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
            <Label className="text-sm">قناة التسويق (اختياري)</Label>
            <p className="text-[10px] text-foreground/60 mt-0.5 mb-2">
              اربط الكوبون بقناة لنسب الإيراد إليها. عندما يستخدم العميل هذا الكوبون — حتى لو أدخله يدويًا — سيُنسب الإيراد لهذه القناة في تحليلات التسويق.
            </p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "", l: "بدون" },
                { v: "whatsapp", l: "WhatsApp" },
                { v: "email", l: "بريد" },
                { v: "instagram", l: "Instagram" },
                { v: "tiktok", l: "TikTok" },
                { v: "other", l: "أخرى" },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setForm({ ...form, channel: o.v })}
                  className={`py-1.5 rounded-md border text-xs ${form.channel === o.v ? "bg-primary text-primary-foreground border-primary" : "bg-white"}`}
                  data-testid={`new-coupon-channel-${o.v || "none"}`}
                >{o.l}</button>
              ))}
            </div>
            {form.channel && (
              <Input
                value={form.campaign}
                onChange={(e) => setForm({ ...form, campaign: e.target.value })}
                placeholder="اسم الحملة (اختياري) — مثال: cart_recovery"
                className="mt-2 text-xs"
              />
            )}
          </div>
          <Button onClick={submit} disabled={submitting} className="w-full" data-testid="new-coupon-submit">
            {submitting ? "جاري الإنشاء..." : "إنشاء الكود"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
